import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, AlertTriangleIcon, XIcon, CheckIcon, FrownIcon, SparklesIcon, ArrowRightIcon, TrendingUpIcon } from 'lucide-react';
const rows = [{
  before: 'Noches enteras buscando ejercicios en YouTube.',
  after: 'Sesiones listas en tu móvil en 2 clics.'
}, {
  before: 'Estrés antes de cada clase por no saber qué proponer.',
  after: 'Seguridad total al entrar al natatorio.'
}, {
  before: 'Explicaciones confusas que los alumnos no siguen.',
  after: 'Soporte visual y metodología clara que encanta a los alumnos.'
}, {
  before: 'Frustración por ver un grupo estancado y poco motivado.',
  after: 'Progresión didáctica clara desde Ambientación a Perfeccionamiento.'
}];
export function ContrastSection() {
  return <section className="w-full bg-bg-main py-20 px-4" data-id="element-105">
      <div className="max-w-5xl mx-auto" data-id="element-106">
        {/* Time cost callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="relative bg-gradient-to-br from-badge-red/15 via-bg-alt to-badge-red/5 border border-badge-red/30 rounded-3xl p-6 sm:p-10 mb-20 overflow-hidden shadow-[0_20px_60px_-15px_rgba(239,68,68,0.25)]" data-id="element-107">
          {/* Decorative red glow */}
          <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 bg-badge-red/20 rounded-full blur-[100px] pointer-events-none" data-id="element-108" />
          <div aria-hidden className="absolute -bottom-24 -left-24 w-64 h-64 bg-badge-red/10 rounded-full blur-[80px] pointer-events-none" data-id="element-109" />

          {/* Top warning stripe */}
          <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-badge-red to-transparent opacity-70" data-id="element-110" />

          <div className="relative z-10" data-id="element-111">
            {/* Eyebrow with pulsing dot */}
            <div className="flex items-center justify-center gap-2 mb-5" data-id="element-112">
              <span className="relative flex h-2.5 w-2.5" data-id="element-113">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-badge-red opacity-75" data-id="element-114" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-badge-red shadow-[0_0_10px_rgba(239,68,68,0.8)]" data-id="element-115" />
              </span>
              <span className="text-badge-red font-bold tracking-[0.2em] text-[10px] sm:text-xs uppercase" data-id="element-116">
                Alerta · Costo Oculto
              </span>
            </div>

            <h3 className="text-text-main font-black text-2xl sm:text-4xl text-center leading-tight tracking-tight mb-2" data-id="element-117">
              Deja de pagar con tu{' '}
              <span className="relative inline-block" data-id="element-118">
                <span className="relative z-10 text-badge-red" data-id="element-119">
                  tiempo libre
                </span>
                <span className="absolute bottom-1 left-0 right-0 h-2 bg-badge-red/20 -z-0" data-id="element-120" />
              </span>
              .
            </h3>
            <p className="text-text-sec text-center text-sm sm:text-base mb-8 sm:mb-10" data-id="element-121">
              El verdadero costo oculto de planificar a mano:
            </p>

            {/* Hero number */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-8 pb-8 border-b border-badge-red/20" data-id="element-122">
              <div className="flex items-baseline gap-2" data-id="element-123">
                <span className="text-badge-red font-black text-6xl sm:text-8xl leading-none tracking-tighter drop-shadow-[0_0_20px_rgba(239,68,68,0.4)]" data-id="element-124">
                  +100
                </span>
                <span className="text-badge-red/70 font-bold text-xl sm:text-2xl" data-id="element-125">
                  hrs
                </span>
              </div>
              <div className="text-center sm:text-left" data-id="element-126">
                <p className="text-text-main font-black text-lg sm:text-xl uppercase tracking-wide leading-tight" data-id="element-127">
                  Perdidas al año
                </p>
                <p className="text-text-sec text-sm sm:text-base" data-id="element-128">
                  de tu vida personal
                </p>
              </div>
            </div>

            {/* Detail grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-129">
              <div className="flex items-start gap-3 bg-black/30 border border-white/5 rounded-xl p-4 backdrop-blur-sm" data-id="element-130">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-badge-red/15 border border-badge-red/30 flex items-center justify-center" data-id="element-131">
                  <ClockIcon className="w-5 h-5 text-badge-red" data-id="element-132" />
                </div>
                <div data-id="element-133">
                  <p className="text-text-main font-bold text-sm sm:text-base leading-snug mb-1" data-id="element-134">
                    2 a 4 horas por semana
                  </p>
                  <p className="text-text-sec text-xs sm:text-sm leading-relaxed" data-id="element-135">
                    diseñando manualmente cada sesión.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-black/30 border border-white/5 rounded-xl p-4 backdrop-blur-sm" data-id="element-136">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-badge-red/15 border border-badge-red/30 flex items-center justify-center" data-id="element-137">
                  <AlertTriangleIcon className="w-5 h-5 text-badge-red" data-id="element-138" />
                </div>
                <div data-id="element-139">
                  <p className="text-text-main font-bold text-sm sm:text-base leading-snug mb-1" data-id="element-140">
                    Fines de semana, noches y familia
                  </p>
                  <p className="text-text-sec text-xs sm:text-sm leading-relaxed" data-id="element-141">
                    tiempo que ya no vuelve.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Comparison section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-142">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6" data-id="element-143">
            <SparklesIcon className="w-4 h-4 text-primary" data-id="element-144" />
            <span className="text-sm font-semibold text-primary tracking-wide uppercase" data-id="element-145">
              La transformación
            </span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight mb-4" data-id="element-146">
            De agotado a{' '}
            <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent" data-id="element-147">
              imparable
            </span>
          </h2>
          <p className="text-text-sec text-lg max-w-2xl mx-auto" data-id="element-148">
            Mira cómo cambia tu día a día con MegaPack Natación a tu lado.
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="bg-bg-alt rounded-3xl shadow-2xl border border-border-light overflow-hidden" data-id="element-149">
          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 border-b border-border-light" data-id="element-150">
            <div className="px-6 py-5 flex items-center gap-3 border-b md:border-b-0 md:border-r border-border-light bg-badge-red/5" data-id="element-151">
              <div className="w-9 h-9 rounded-lg bg-badge-red/15 border border-badge-red/30 flex items-center justify-center flex-shrink-0" data-id="element-152">
                <FrownIcon className="w-5 h-5 text-badge-red" data-id="element-153" />
              </div>
              <div data-id="element-154">
                <p className="text-[10px] font-bold text-badge-red/70 uppercase tracking-[0.2em]" data-id="element-155">
                  Hoy · Sin MegaPack
                </p>
                <p className="text-base font-black text-text-main" data-id="element-156">
                  Lo que te cuesta
                </p>
              </div>
            </div>
            <div className="px-6 py-5 flex items-center gap-3 bg-gradient-to-r from-primary/10 to-primary/5 relative overflow-hidden" data-id="element-157">
              <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent pointer-events-none" data-id="element-158" />
              <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0 relative z-10 shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-id="element-159">
                <TrendingUpIcon className="w-5 h-5 text-primary" data-id="element-160" />
              </div>
              <div className="relative z-10" data-id="element-161">
                <p className="text-[10px] font-bold text-primary uppercase tracking-[0.2em]" data-id="element-162">
                  Mañana · Con MegaPack
                </p>
                <p className="text-base font-black text-text-main" data-id="element-163">
                  Tu nueva realidad
                </p>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-border-light" data-id="element-164">
            {rows.map((row, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-30px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="grid grid-cols-1 md:grid-cols-2 group relative" data-id="element-165">
                {/* Before column */}
                <div className="px-6 py-6 flex items-start gap-4 border-b md:border-b-0 md:border-r border-border-light bg-black/20 group-hover:bg-badge-red/5 transition-colors" data-id="element-166">
                  <div className="w-7 h-7 rounded-full bg-badge-red/15 flex items-center justify-center flex-shrink-0 mt-0.5 border border-badge-red/30" data-id="element-167">
                    <XIcon className="w-4 h-4 text-badge-red" strokeWidth={3} data-id="element-168" />
                  </div>
                  <p className="text-text-sec font-medium leading-relaxed" data-id="element-169">
                    {row.before}
                  </p>
                </div>

                {/* Transformation arrow (desktop only) */}
                <div aria-hidden className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none" data-id="element-170">
                  <div className="w-8 h-8 rounded-full bg-bg-alt border border-primary/40 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.3)] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110" data-id="element-171">
                    <ArrowRightIcon className="w-4 h-4 text-primary" strokeWidth={3} data-id="element-172" />
                  </div>
                </div>

                {/* After column */}
                <div className="px-6 py-6 flex items-start gap-4 bg-primary/5 relative overflow-hidden group-hover:bg-primary/10 transition-colors" data-id="element-173">
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-174" />
                  <div className="w-7 h-7 rounded-full bg-check-green/20 flex items-center justify-center flex-shrink-0 mt-0.5 border border-check-green/40 relative z-10 shadow-[0_0_8px_rgba(16,185,129,0.2)]" data-id="element-175">
                    <CheckIcon className="w-4 h-4 text-check-green" strokeWidth={3} data-id="element-176" />
                  </div>
                  <p className="text-text-main font-bold relative z-10 leading-relaxed" data-id="element-177">
                    {row.after}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Footer payoff */}
          <div className="px-6 py-6 bg-gradient-to-r from-primary/10 via-cyan-500/5 to-primary/10 border-t border-primary/20 text-center" data-id="element-178">
            <p className="text-text-main font-black text-lg sm:text-xl" data-id="element-179">
              Recuperas tu tiempo,{' '}
              <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent" data-id="element-180">
                tu energía y tu pasión por enseñar.
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}